import type {
  IPrimitivePaneView,
  ISeriesApi,
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
} from "lightweight-charts";

interface BitmapTarget {
  useMediaCoordinateSpace(fn: (scope: { context: CanvasRenderingContext2D }) => void): void;
}

export interface RangeHaloPrimitiveDatum {
  time: Time;
  value: number;
  color: string;
  widthMultiplier: 1.6 | 1.8 | 2.4;
}

/** Draws the absorption layer wider than LWC's delta histogram, behind that histogram. */
export class RangeHaloPrimitive implements ISeriesPrimitive<Time> {
  private attachedTo: SeriesAttachedParameter<Time> | null = null;
  private bars: readonly RangeHaloPrimitiveDatum[] = [];
  private readonly view: IPrimitivePaneView = {
    zOrder: () => "normal",
    renderer: () => ({ draw: (target) => this.draw(target as unknown as BitmapTarget) }),
  };

  constructor(
    private readonly series: ISeriesApi<"Histogram", Time>,
    private readonly onWidths?: (widths: readonly number[]) => void,
  ) {}

  attached(param: SeriesAttachedParameter<Time>) {
    this.attachedTo = param;
  }

  detached() {
    this.attachedTo = null;
  }

  update(bars: readonly RangeHaloPrimitiveDatum[]) {
    this.bars = bars;
    this.attachedTo?.requestUpdate();
  }

  paneViews(): readonly IPrimitivePaneView[] {
    return [this.view];
  }

  private draw(target: BitmapTarget) {
    const attached = this.attachedTo;
    if (!attached || !this.bars.length) return;
    const timeScale = attached.chart.timeScale();
    const visible = timeScale.getVisibleLogicalRange();
    const visibleSlots = visible === null ? 0 : Number(visible.to) - Number(visible.from);
    const slotWidth = visibleSlots > 0 ? timeScale.width() / visibleSlots : 6;

    target.useMediaCoordinateSpace(({ context }) => {
      const widths: number[] = [];
      const zero = this.series.priceToCoordinate(0);
      if (zero === null) return;
      context.save();
      for (const bar of this.bars) {
        const x = timeScale.timeToCoordinate(bar.time);
        const value = this.series.priceToCoordinate(bar.value);
        if (x === null || value === null) continue;
        const width = slotWidth * bar.widthMultiplier;
        widths.push(width);
        context.fillStyle = bar.color;
        context.fillRect(x - width / 2, Math.min(zero, value), width, Math.max(1, Math.abs(value - zero)));
      }
      context.restore();
      this.onWidths?.(widths);
    });
  }
}
